import { Link } from "react-router-dom";
import { ArrowUpRight, Scales, Robot, Clock, Sparkle } from "@phosphor-icons/react";
import { BRAND_NAME, BRAND_KICKER, BRAND_FOOTER, BRAND_COPYRIGHT_YEAR } from "@/lib/brand";

export default function Landing() {
    return (
        <div className="min-h-screen bg-white text-slate-900">
            {/* Nav */}
            <header className="border-b border-slate-200">
                <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div className="font-display text-2xl font-extrabold tracking-tight">{BRAND_NAME}</div>
                        <span className="hidden sm:inline text-[10px] font-mono uppercase tracking-[0.2em] text-slate-400 border-l border-slate-200 pl-3 ml-1">
                            {BRAND_KICKER}
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <Link to="/intake" className="btn-ghost" data-testid="public-intake-link">For Clients →</Link>
                        <Link to="/login" className="btn-secondary" data-testid="landing-login">Sign in</Link>
                        <Link to="/register" className="btn-primary hidden sm:inline-block" data-testid="landing-register">
                            Start trial
                        </Link>
                    </div>
                </div>
            </header>

            {/* Hero */}
            <section className="max-w-6xl mx-auto px-6 pt-20 lg:pt-28 pb-16 grid lg:grid-cols-12 gap-10">
                <div className="lg:col-span-7">
                    <div className="text-xs font-mono uppercase tracking-[0.2em] text-blue-600 mb-5">
                        <Sparkle size={12} weight="fill" className="inline mr-1.5 -mt-0.5" />
                        AI-native legal practice
                    </div>
                    <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-[0.95] tracking-tight text-[#0B2447]">
                        The operating system<br />for modern law firms.
                    </h1>
                    <p className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed">
                        One workspace for cases, clients, deadlines, documents, and billing —
                        with Claude Sonnet 4.5 baked in for contract review and case summaries.
                    </p>
                    <div className="mt-8 flex flex-wrap gap-3">
                        <Link to="/register" data-testid="hero-start-trial" className="btn-primary inline-flex items-center gap-2">
                            Start free trial <ArrowUpRight size={16} weight="bold" />
                        </Link>
                        <Link to="/login" data-testid="hero-demo-login" className="btn-secondary">
                            See live demo
                        </Link>
                    </div>
                    <div className="mt-10 text-xs font-mono text-slate-500 uppercase tracking-[0.18em]">
                        Trusted by independent counsel · personal injury · M&A · estate planning
                    </div>
                </div>

                <div className="lg:col-span-5">
                    <div className="card-sharp p-6 shadow-sm">
                        <div className="text-xs font-mono uppercase tracking-[0.18em] text-slate-500 mb-4">
                            What you ship in week one
                        </div>
                        <ul className="space-y-4">
                            {[
                                { icon: Scales, t: "Case & matter management", d: "Status pipelines, activity timeline, client profiles." },
                                { icon: Robot, t: "AI contract review", d: "Claude extracts clauses, deadlines, risks in seconds." },
                                { icon: Clock, t: "Time + invoice in one click", d: "Sticky timer per case, instant downloadable invoice." },
                            ].map((f, i) => (
                                <li key={i} className="flex gap-3">
                                    <div className="w-9 h-9 rounded-sm bg-[#0B2447] text-white flex items-center justify-center shrink-0">
                                        <f.icon size={18} weight="duotone" />
                                    </div>
                                    <div>
                                        <div className="font-semibold text-sm">{f.t}</div>
                                        <div className="text-xs text-slate-600 mt-0.5">{f.d}</div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            <footer className="border-t border-slate-200 mt-10">
                <div className="max-w-6xl mx-auto px-6 py-8 flex flex-col sm:flex-row gap-3 sm:items-center justify-between text-xs text-slate-500">
                    <div>© {BRAND_COPYRIGHT_YEAR} {BRAND_NAME}. {BRAND_FOOTER}</div>
                    <div className="flex gap-4 font-mono uppercase tracking-[0.15em]">
                        <Link to="/intake" className="hover:text-slate-900">Client intake</Link>
                        <Link to="/login" className="hover:text-slate-900">Sign in</Link>
                    </div>
                </div>
            </footer>
        </div>
    );
}
